import type { FavoriteViewFolder } from '@/api/favorites'
import { VpButton, VpDivider } from '@vtmn-play/react'
import { useTranslation } from 'react-i18next'
import { ALL_SAVED_VIEWS_ID, FavoriteViewFolderCard } from '@/components/FavoriteViews/FavoriteViewFolderCard'
import { FavoriteViewFolderFields } from '@/components/FavoriteViews/FavoriteViewFolderFields'

interface FavoriteViewFolderDraft {
  isCreating: boolean
  name: string
  selectedEmoji: string | null
}

interface FavoriteViewFolderSelectorProps {
  folders: FavoriteViewFolder[]
  totalViews: number
  activeFolderId: string
  hasLoadError: boolean
  draft: FavoriteViewFolderDraft
  onAddFolder: () => void
  onCancelDraft: () => void
  onDraftNameChange: (name: string) => void
  onEmojiSelect: (unified: string) => void
  onFolderSelect: (folderId: string) => void
}

export function FavoriteViewFolderSelector({
  folders,
  totalViews,
  activeFolderId,
  hasLoadError,
  draft,
  onAddFolder,
  onCancelDraft,
  onDraftNameChange,
  onEmojiSelect,
  onFolderSelect,
}: Readonly<FavoriteViewFolderSelectorProps>) {
  const { t } = useTranslation()

  return (
    <section className="favorite-view-panel__folders" aria-labelledby="favorite-view-folders-title">
      <div className="favorite-view-panel__folders-header">
        <h2 id="favorite-view-folders-title">{t('favorite-views.panel.my-folders')}</h2>
        <VpButton
          type="button"
          variant="secondary"
          size="small"
          aria-expanded={draft.isCreating}
          aria-controls="favorite-view-new-folder"
          onClick={draft.isCreating ? onCancelDraft : onAddFolder}
          data-testid="favorite-view-add-folder"
        >
          {`+ ${t('favorite-views.panel.add-folder')}`}
        </VpButton>
      </div>
      <VpDivider />
      {hasLoadError && <p role="alert">{t('favorite-views.errors.load-folders')}</p>}
      <ul className="favorite-view-panel__folder-list" aria-label={t('favorite-views.panel.my-folders')}>
        {draft.isCreating && (
          <li id="favorite-view-new-folder" className="favorite-view-panel__new-folder-row" data-testid="favorite-view-new-folder-row">
            <div className="favorite-view-panel__new-folder">
              <FavoriteViewFolderFields
                name={draft.name}
                selectedEmoji={draft.selectedEmoji}
                onNameChange={onDraftNameChange}
                onEmojiSelect={onEmojiSelect}
                onCancel={onCancelDraft}
              />
            </div>
          </li>
        )}
        <FavoriteViewFolderCard
          folder={{
            id: ALL_SAVED_VIEWS_ID,
            name: t('favorite-views.panel.all-saved-views'),
            emoji: null,
            viewCount: totalViews,
          }}
          isSelected={activeFolderId === ALL_SAVED_VIEWS_ID}
          onSelect={() => onFolderSelect(ALL_SAVED_VIEWS_ID)}
        />
        {folders.map(folder => (
          <FavoriteViewFolderCard
            key={folder.id}
            folder={folder}
            isSelected={activeFolderId === folder.id}
            onSelect={() => onFolderSelect(folder.id)}
          />
        ))}
      </ul>
    </section>
  )
}
